import { useState, useEffect, useRef, type ReactNode } from 'react';
import * as s from './TeaserWindow.css';

interface TeaserWindowProps {
  viewId: string;
  viewLabel: string;
  glitchContent?: ReactNode;
}

type Phase = 'log' | 'glitch' | 'lost';

const LOG_LINES = (viewId: string) => [
  { text: `> INITIALIZING MODULE [${viewId}]...`, type: 'module' as const },
  { text: '> SCANNING DATA FRAGMENTS...', type: 'normal' as const },
  { text: '> DECRYPTING INTERFACE...', type: 'normal' as const },
  { text: '> SIGNAL STRENGTH: ▓▓▓▓▓░░░░░ 47%', type: 'warning' as const },
  { text: '> ATTEMPTING CONNECTION...', type: 'normal' as const },
];

export function TeaserWindow({ viewId, glitchContent }: TeaserWindowProps) {
  const [phase, setPhase] = useState<Phase>('log');
  const [visibleLines, setVisibleLines] = useState(0);
  const timersRef = useRef<ReturnType<typeof setTimeout>[]>([]);
  const lines = LOG_LINES(viewId);

  useEffect(() => {
    // Reset on mount (re-open 시 매번 재생)

    timersRef.current.forEach(clearTimeout);
    timersRef.current = [];

    // 라인 순차 표시
    lines.forEach((_, i) => {
      const timer = setTimeout(
        () => {
          setVisibleLines(i + 1);
        },
        (i + 1) * 400
      );
      timersRef.current.push(timer);
    });

    // 글리치 페이즈
    const glitchTimer = setTimeout(
      () => {
        if (glitchContent) {
          setPhase('glitch');
        }
        // lost 페이즈
        const lostTimer = setTimeout(
          () => {
            setPhase('lost');
          },
          glitchContent ? 300 : 0
        );
        timersRef.current.push(lostTimer);
      },
      (lines.length + 1) * 400
    );
    timersRef.current.push(glitchTimer);

    return () => {
      timersRef.current.forEach(clearTimeout);
      timersRef.current = [];
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- 마운트 때 한 번만 재생하는 연출이다. viewId·glitchContent 가 바뀌어도 도는 타이머를 다시 걸지 않는다
  }, []);

  return (
    <div className={s.container}>
      <div className={s.scanlineOverlay} aria-hidden="true" />

      {phase === 'glitch' && glitchContent && (
        <div className={s.glitchOverlay} aria-hidden="true">
          {glitchContent}
        </div>
      )}

      <div className={s.terminal} role="log" aria-live="polite">
        {lines.slice(0, visibleLines).map((line, i) => (
          <div key={i} className={`${s.logLine} ${s.logLineVisible}`}>
            {line.type === 'module' ? (
              <span className={s.moduleHighlight}>{line.text}</span>
            ) : line.type === 'warning' ? (
              <span className={s.warningHighlight}>{line.text}</span>
            ) : (
              line.text
            )}
          </div>
        ))}

        {phase === 'lost' && (
          <div className={`${s.logLine} ${s.logLineVisible}`}>
            <span className={s.signalLost}>&gt; [SIGNAL LOST]</span>
          </div>
        )}

        {phase !== 'lost' && visibleLines > 0 && <span className={s.cursor} aria-hidden="true" />}
      </div>
    </div>
  );
}
